<template>
  <tile class="opc-addon-config additional-template">
    <foldable v-model="isExpanded" :arrow="hasSelectedPlan" :edit="hasSelectedPlan" :title="title">
      <template #head>
        <div class="opc-zm-base-card__head">
          <div class="opc-zm-base-card__head-left">
            <img :src="logo" class="opc-zm-base-card__logo" :alt="title + ' Icon'" tabindex="-1" />
          </div>
          <div class="opc-zm-base-card__head-right">
            <h2 class="opc-zm-base-card__name text-capitalize">
              {{ title }}
            </h2>
            <div v-if="!isExpanded && hasSelectedPlan" class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
              <ul>
                <li>{{ collapseDisplayText }}</li>
              </ul>
            </div>
          </div>
        </div>
      </template>
      <div class="divide-line"></div>
      <div class="additional-template__desc">
        <p class="additional-template__usage-desc">{{ description }}</p>
        <p class="additional-template__plan-desc">{{ desc }}</p>
      </div>
      <div class="additional-template__input-number">
        <input-number-with-popover class="additional-template__input-number-selector" ref="userInput" v-model="hosts" size="lg"
          controls-position="both" :min="minHosts" :max="maxHosts" @change="changeHosts"
          :label="trackingPlanName + ' Select the number of users'" :decrease-tips="decreaseTips"
          :data-testid="`checkout-input-license-${selectedPlanCode}`" />
        <span class="additional-template__input-number-desc">{{ unitWithPlural }}</span>
        <zm-tooltip disallow-learn-about :aria-label="'Learn more about ' + unitWithPlural" placement="bottom" @show="toggleTips(true)" @hide="toggleTips(false)">
          <template #content>
            <div v-html="tips"></div>
          </template>
          <tips-icon class="icon_tips_additional_host" type="info" :aria-label="'Learn more about ' + unitWithPlural" />
        </zm-tooltip>
      </div>
      <zm-banner show-close v-if="alertMessage.message" class="additional-template__alert" :type="alertMessage.type">
        <span v-html="alertMessage.message" />
      </zm-banner>
      <div class="opc-addon-config__main">
        <div class="opc-addon-config__main__title">
          <p class="subscription-cycle__title">{{ $t('billing.select_billing_cycle') }}</p>
          <billed-billing-cycle-radio
            v-model="selectedBillingCycle"
            :billingCycleList="billingCycleOptions"
            :unit="unit"
            @change="onPlanSelect"
          />
        </div>
      </div>
      <div class="opc-zm-base-card__main__footer" v-if="showRemove">
        <zm-button
          data-testid="checkout-button-43"
          type="text"
          @click="removePlanByKey"
          :aria-label="`remove ${title} plan`"
          >
          {{ $t('billing.common.remove') }}
        </zm-button>
      </div>
      <preview-payment class="additional-template__preview-payment" />
    </foldable>
  </tile>
</template>
<script>
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import BilledBillingCycleRadio from "@/components/service/config-billing-cycle/billed-cycle/index.vue";
import plansMixin from '@/plans/mx/plan';
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import { mapGetters, mapMutations, mapActions, mapState } from 'vuex';
import { planTypes } from '@/common/constants';
import { flowTypes } from '@/common/service/page-config.js';
import { get } from 'lodash';
import { pageConfig } from '@/common/service/page-config';
import { dateTimeFormat } from '@/utils/dateformat.js';
import PreviewPayment from '@/views/checkout/payment/preview-payment';
import autofill from "@/plans/mx/autofill";
import { getPlanUnitByQuantity, planUnitMap } from '@/common/product-unit';
import { getPlanCollapseText } from '@/common/product';
import { getTrackingProductNameByPlanType } from '@/aspects/trackings';

export default {
  name: 'Basic',
  components: { PreviewPayment, Tile, Foldable, InputNumberWithPopover, BilledBillingCycleRadio },
  mixins: [plansMixin, autofill],
  props: {
    title: String,
    logo: String,
    description: String,
    desc: String,
    planType: String,
  },
  data() {
    return {
      isExpanded: true,
      selectedPlanCode: '',
      selectedBillingCycle: '',
      hosts: 1,
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'basicConfig', 'currentEditPlan', 'assignInfo', 'boughtPlans']),
    ...mapState({
      planMapFlat: (state) => state.ShoppingCart.planMapFlat,
    }),
    unit() {
      return planUnitMap[this.planType];
    },
    unitWithPlural() {
      // first letter need to uppercase
      return this.planType === planTypes.ADDITIONAL_HOST ? this.$t('plan.unit_first_letter_uppercase.hub_host', this.hosts) : this.$t('plan.unit_first_letter_uppercase.co_editor', this.hosts);
    },
    collapseDisplayText() {
      if (!this.cartSelectedPlan) return '';
      const quantity = this.cartSelectedPlan?.cartInfo?.selected_number || 0;
      const unitDesc = getPlanUnitByQuantity(this.unit, quantity).toLowerCase();
      return getPlanCollapseText({
        amount: this.cartSelectedPlan?.amount,
        billingCycle: this.cartSelectedPlan?.billing_cycle,
        quantity: quantity,
        cycleValue: this.cartSelectedPlan?.cycleValue,
        unit: this.unit,
        customUnitDesc: unitDesc,
      });
    },
    isEditFlow() {
      return this.$context.flow === flowTypes.edit && this.$context.is.editPage;
    },
    hasSelectedPlan() {
      return !!this.selectedPlanCode;
    },
    minHosts() {
      if (this.isEditFlow && this.basicConfig.downSellData.disableDownSell || !this.isStandardPrice) {
        return get(this.currentEditPlan, 'cartInfo.selected_number', 0);
      }
      return this.cartSelectedPlan?.productInfo?.min || 1;
    },
    maxHosts() {
      return this.cartSelectedPlan?.productInfo?.max || 20;
    },
    planList() {
      return this.planMapFlat.filter((p) => p.type === this.planType && p.isCurrentCategory);
    },
    getUnit() {
      // type = ['collapse', 'cycle']
      return (cycle, type = 'cycle', planType = this.planType) => {
        const units = {
          collapse: {
            [planTypes.ADDITIONAL_HOST]: {
              monthly: this.$t('billing.events.addon.monthly_hub_host'),
              annual: this.$t('billing.events.addon.annual_hub_host'),
              quarterly: this.$t('billing.events.addon.quarterly_hub_host'),
              two_years: this.$t('billing.events.addon.two_years_hub_host'),
              three_years: this.$t('billing.events.addon.three_years_hub_host'),
            },
            [planTypes.ADDITIONAL_CO_EDITOR]: {
              monthly: this.$t('billing.events.addon.monthly_co_editor'),
              annual: this.$t('billing.events.addon.annual_co_editor'),
              quarterly: this.$t('billing.events.addon.quarterly_co_editor'),
              two_years: this.$t('billing.events.addon.two_years_co_editor'),
              three_years: this.$t('billing.events.addon.three_years_co_editor'),
            },
          },
          cycle: {
            [planTypes.ADDITIONAL_HOST]: {
              monthly: this.$t('billing.events.addon.host_per_month'),
              annual: this.$t('billing.events.addon.host_per_month'),
              quarterly: this.$t('billing.events.addon.host_per_month'),
              two_years: this.$t('billing.events.addon.host_per_month'),
              three_years: this.$t('billing.events.addon.host_per_month'),
            },
            [planTypes.ADDITIONAL_CO_EDITOR]: {
              monthly: this.$t('billing.events.addon.co_editor_per_month'),
              annual: this.$t('billing.events.addon.co_editor_per_month'),
              quarterly: this.$t('billing.events.addon.co_editor_per_month'),
              two_years: this.$t('billing.events.addon.co_editor_per_month'),
              three_years: this.$t('billing.events.addon.co_editor_per_month'),
            },
          },
        };
        return units[type][planType][cycle];
      };
    },
    billingCycleOptions() {
      const addonPlanName = this.title;
      return this.planList.map((p) => {
        const dependPlanList = p.billingCycleDependBasePlanTypeList;
        // based on zoom sass
        let isZoomSass = this.boughtPlans.filter(p => !p.isPPA && dependPlanList.includes(p.type) && !p.isFreeTrial)
          .sort((a, b) => a.cycleValue - b.cycleValue)[0];
        if (!isZoomSass) {
          isZoomSass = this.getSelectedPlans.find(p => dependPlanList.includes(p.type))
        }
        const basePlanName = isZoomSass && this.$t(isZoomSass.typeNameKey);
        const isBulkLicenseAccountDisabled =
          this.isEditFlow &&
          this.basicConfig.isBulkLicenseAccount &&
          get(this.currentEditPlan, ['billing_cycle'], '') !== p.billing_cycle;
        const bulkTooltip = isBulkLicenseAccountDisabled ? this.$t('billing.error.buld_acount_license') : '';
        const isDisabledExceedCycle = p.cycleValue > isZoomSass?.cycleValue;
        const isDisabled = isDisabledExceedCycle || isBulkLicenseAccountDisabled;
        const disabledTooltip = isBulkLicenseAccountDisabled ? bulkTooltip :
          isDisabledExceedCycle ? this.$t('billing.planform.annual_addon_disable_tooltip', [basePlanName, addonPlanName, basePlanName]) : '';

        return { plan: p, disabled: isDisabled, disabledTooltip };
      }).reverse();
    },
    trackingPlanKey() {
      return getTrackingProductNameByPlanType(this.planType);
    },
    cartSelectedPlan() {
      return get(
        this.getSelectedPlans.filter((p) => p.type === this.planType),
        [0],
      );
    },
    showIcon() {
      return this.showRemove && this.hasSelectedPlan;
    },
    showRemove() {
      return this.$context.is.buyNow || this.$context.is.basic2pro || this.$context.is.trial2paid;
    },
    decreaseTips() {
      const downSellData = this.basicConfig.downSellData;
      const previousHosts = get(this.currentEditPlan, 'cartInfo.selected_number', 0);
      if (this.isEditFlow && this.hosts === previousHosts) {
        if (downSellData.disableDownSell) {
          return this.$t('billing.error.can_not_bought_plan_tips');
        }

        return this.forNonStandardPriceTips;
      }

      return '';
    },
    tips() {
      return this.planType === planTypes.ADDITIONAL_HOST
        ? this.$t('billing.events.addon.ze_hub_hosts_tips')
        : this.$t('billing.events.addon.zs_co_editors_tips');
    },
    alertMessage() {
      if (this.hosts >= this.maxHosts) {
        const unitText = getPlanUnitByQuantity(this.unit, this.maxHosts);
        return {
          message: this.$t('billing.events.addon.maximun_alert_message', [unitText]),
          type: 'warning'
        }
      }
      // downgrade case
      if (this.isEditFlow) {
        const { assignLicenseQuantity = 0, planExpireTime } = get(this.assignInfo, pageConfig.oldPlanCode, {});

        if (assignLicenseQuantity - this.hosts > 0) {
          const needAssignQuantity = assignLicenseQuantity - this.hosts;
          return {
            message: this.$t('billing.error.downgrade_alert_for_ze_addon', [
              needAssignQuantity,
              this.planType === planTypes.ADDITIONAL_HOST
                ? this.$t('billing.events.addon.hosts')
                : this.$t('billing.events.addon.co_editors'),
              dateTimeFormat(planExpireTime),
            ]),
            type: 'info',
          };
        }
      }
      return {};
    },
    trackingPlanName() {
      return this.$t('billing.planform.title.' + this.trackingPlanKey);
    }
  },
  mounted() {
    this.$context.$on('collapse-plan-card', this.collapseCard);
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'removePlan']),
    ...mapActions(['switchSelectPlan']),
    onPlanSelect(oldPlan, newPlan) {
      const oldPlanCode = this.selectedPlanCode;
      const newPlanCode = newPlan?.plan_code;

      if (this.selectedPlanCode !== newPlanCode) {
        if (oldPlanCode) {
          this.switchSelectPlan([oldPlanCode, newPlanCode, undefined]);
        } else {
          let quantity = this.hosts;
          if (quantity) {
            quantity = quantity <= this.maxHosts ? quantity : this.maxHosts;
          } else {
            quantity = this.minHosts;
          }
          this.hosts = quantity;
          this.updateSelectPlan({
            plan_code: newPlanCode,
            cartInfo: { selected_number: quantity },
          });
        }
        this.selectedPlanCode = newPlanCode;
        this.selectedBillingCycle = newPlan?.billing_cycle || '';
        // event tracking
        const billingCycle = this.cartSelectedPlan?.billing_cycle;
        billingCycle &&
          this.$tracking.addOnBuyInteraction(
            `${this.trackingPlanKey} billing cycle`,
            this.$tEn(`billing.billing_cycle.${billingCycle}`)?.toLowerCase(),
          );
      }
    },
    changeHosts(newHosts) {
      const planCode = this.selectedPlanCode;
      const oldHosts = this.hosts;

      if (planCode) {
        this.updateSelectPlan({
          plan_code: planCode,
          cartInfo: { selected_number: newHosts },
        });
      }

      this.hosts = newHosts;
      // event tracking
      planCode &&
        this.$tracking.addOnBuyInteraction(
          `${this.trackingPlanKey} hosts`,
          `${newHosts > oldHosts ? 'increase' : 'decrease'} - ${newHosts}`,
        );
    },
    // For A11Y
    getFirstFocusableControl() {
      return Promise.resolve(this.$refs.userInput);
    },
    collapseCard() {
      this.isExpanded = false;
    },
    toggleTips(val) {
      if (val) {
        this.$tracking.buyInteraction(
          'zoom events add-ons',
          `${this.trackingPlanKey} - tooltip`,
        );
      }
    },
  },
  watch: {
    cartSelectedPlan: {
      immediate: true,
      handler(plan) {
        this.selectedPlanCode = plan?.plan_code ?? '';
        this.selectedBillingCycle = plan?.billing_cycle ?? '';
        this.hosts = plan?.cartInfo?.selected_number ?? this.minHosts;
      },
    },
    isExpanded: {
      handler(val) {
        this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} accordion`, val ? 'expand' : 'collapse');
      },
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/styles/zoom-card.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

.additional-template {
  .opc-zm-base-card__name {
    @include media(mobile) {
      display: block;
      padding-right: 60px;
    }
  }

  &__alert {
    margin-top: 12px;
  }

  &__usage-desc,
  &__plan-desc {
    color: var(--zoom-color-text-stronger-neutral);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
  }

  &__plan-desc {
    margin-top: 20px;
  }

  &__input-number {
    margin-top: 20px;
    display: flex;
    align-items: center;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    &-desc {
      margin-left: 8px;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 120%;
    }
  }

  &__preview-payment {
    margin-top: 24px;
  }

  // Billing cycle section styles
  .opc-addon-config__main {

    &__title {
      margin-top: 16px;

      .subscription-cycle__title {
        font-weight: 600;
        font-size: 16px;
        line-height: 150%;
        color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
        margin-bottom: 8px;
      }
    }
  }
}
.icon_tips_additional_host{
  transform: translateY(1px);
}
</style>
